/**
 * TODO migration BaseDrawer (audit B3, 2026-05-28) : ce drawer utilise
 * encore son propre shell (state open + useEffect slide + useEffect ESC +
 * fonction close()). Il devrait être migré vers BaseDrawer comme
 * ProgrammeEditDrawer.
 *
 * Pas fait dans la PR 4 parce que :
 * - Pattern autosave (AUTOSAVE_DELAY_MS) entrelacé au close()
 * - DiffModal rendu en parallèle du drawer, à préserver hors du shell
 * - 405 lignes, refacto risqué sans test e2e
 *
 * À planifier dans le brief V3 quand le pattern BaseDrawer sera stabilisé
 * sur 3+ spécialisations (Programme déjà migré, Veille et Templates à
 * venir). Ne PAS migrer en isolation, ça doit faire partie d'un sprint
 * "stabilisation drawers" avec test e2e Playwright.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Fund } from '../types';
import { updateFund, deleteFund } from '../lib/api';
import { IconClose, IconCheck, IconRefresh } from '../components/icons';
import { initials, logoColor } from '../lib/format';
import { useToast } from '../lib/toast';
import { DiffModal, shouldSkipDiff } from '../components/DiffModal';

const AUTOSAVE_DELAY_MS = 1500;
const DIFF_SKIP_KEY = 'fund-edit';

const FIELD_LABELS: Record<string, string> = {
  name: 'Name', city: 'City', country: 'Country', type: 'Type', category: 'Category',
  founded: 'Founded', vintage: 'Vintage', vintageComment: 'Vintage comment',
  ticketLocal: 'Ticket (local)', ticketUsd: 'Ticket (USD)', ticketMin: 'Ticket min',
  ticketMax: 'Ticket max', fundSize: 'Fund size', fundSizeUsd: 'Fund size USD',
  primaryGeography: 'Primary geography', stages: 'Stages', sectors: 'Sectors',
  portfolioNotable: 'Portfolio companies', portfolioUrl: 'Portfolio URL',
  lead: 'Lead behaviour', leadComment: 'Lead comment', shortThesis: 'Short thesis',
  quantumConviction: 'Quantum fit', status: 'Status', shortlist: 'Shortlist',
  lastVerified: 'Last verified', verificationSource: 'Verification source',
  website: 'Website', linkedin: 'LinkedIn', logoUrl: 'Logo URL',
};

type Tab = 'identity' | 'investment' | 'thesis' | 'meta';

interface Props {
  fund: Fund;
  onClose: () => void;
  onSaved: (f: Fund) => void;
  onDeleted: (id: string) => void;
}

const STAGE_OPTIONS = ['Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C', 'Growth', 'Late stage'];
const TYPE_OPTIONS = ['VC Firm', 'Corporate VC', 'PE Firm', 'Family Office', 'Angel'];
const CATEGORY_OPTIONS = ['Deep Tech', 'Generalist', 'Pure-play Quantum', 'AI / Data Science', 'Climate', 'Cybersecurity', 'Enterprise SaaS'];
const SHORTLIST_OPTIONS = ['Approved', 'Draft'];

export function InvestorEditDrawer({ fund, onClose, onSaved, onDeleted }: Props) {
  const [tab, setTab] = useState<Tab>('identity');
  const [draft, setDraft] = useState<Fund>(fund);
  // `lastSaved` tracks the most-recently persisted snapshot of the fund so the
  // autosave loop only PATCHes when there's a real diff vs the server.
  const [lastSaved, setLastSaved] = useState<Fund>(fund);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thesisLang, setThesisLang] = useState<'en' | 'fr'>('en');
  const [open, setOpen] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const toast = useToast();
  const autosaveRef = useRef<number | null>(null);

  // Slide-in animation: mark "open" on mount, animate "out" on close.
  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 10);
    return () => clearTimeout(t);
  }, []);

  function close() {
    setOpen(false);
    setTimeout(onClose, 220);
  }

  // Press Escape to close - Mac standard for slide-overs / modals.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track changes vs the last persisted snapshot. Used by the autosave loop
  // and to disable Save when nothing changed.
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(lastSaved), [draft, lastSaved]);

  // Autosave: 1.5s debounce after the user stops typing. Cancels on close.
  useEffect(() => {
    if (!dirty || saving || deleting) return;
    if (autosaveRef.current) window.clearTimeout(autosaveRef.current);
    autosaveRef.current = window.setTimeout(() => { void onSave({ silent: true }); }, AUTOSAVE_DELAY_MS);
    return () => { if (autosaveRef.current) window.clearTimeout(autosaveRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  function set<K extends keyof Fund>(key: K, val: Fund[K]) {
    setDraft(prev => ({ ...prev, [key]: val }));
  }
  function setThesis(lang: 'en' | 'fr', val: string) {
    setDraft(prev => ({ ...prev, thesis: { ...(prev.thesis ?? {}), [lang]: val } }));
  }
  function toggleArrItem(key: 'stages' | 'sectors', item: string) {
    setDraft(prev => {
      const arr = new Set(prev[key] ?? []);
      if (arr.has(item)) arr.delete(item);
      else arr.add(item);
      return { ...prev, [key]: Array.from(arr) };
    });
  }

  /** Fire the actual PATCH. Used by both manual save (after diff confirmation) and autosave. */
  async function commitSave(opts?: { silent?: boolean }) {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      const diff = diffFund(lastSaved, draft);
      const res = await updateFund(fund.id, diff);
      setLastSaved(res.fund);
      setSavedAt(Date.now());
      if (!opts?.silent) {
        onSaved(res.fund);
        toast.success('Changes saved');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      if (!opts?.silent) toast.error('Save failed', msg);
    } finally {
      setSaving(false);
    }
  }

  /** Used by manual Save button: optionally show diff confirmation. */
  async function onSave(opts?: { silent?: boolean }) {
    if (!dirty || saving) return;
    if (!opts?.silent && !shouldSkipDiff(DIFF_SKIP_KEY)) {
      setShowDiff(true);
      return;
    }
    await commitSave(opts);
  }

  // Compute the diff for the modal display each render.
  const diffEntries = useMemo(() => {
    if (!showDiff) return [];
    const out: { key: string; label: string; before: unknown; after: unknown }[] = [];
    const allKeys = new Set<string>([...Object.keys(lastSaved), ...Object.keys(draft)]);
    for (const k of allKeys) {
      const a = (lastSaved as unknown as Record<string, unknown>)[k];
      const b = (draft as unknown as Record<string, unknown>)[k];
      if (JSON.stringify(a) === JSON.stringify(b)) continue;
      // Special-case thesis to flatten into EN / FR rows.
      if (k === 'thesis') {
        const ae = (a as { en?: string } | undefined)?.en;
        const af = (a as { fr?: string } | undefined)?.fr;
        const be = (b as { en?: string } | undefined)?.en;
        const bf = (b as { fr?: string } | undefined)?.fr;
        if (JSON.stringify(ae) !== JSON.stringify(be)) out.push({ key: 'thesisEn', label: 'Thesis EN', before: ae, after: be });
        if (JSON.stringify(af) !== JSON.stringify(bf)) out.push({ key: 'thesisFr', label: 'Thesis FR', before: af, after: bf });
        continue;
      }
      if (k === 'id' || k === 'updatedAt' || k === 'location' || k === 'slug') continue;
      out.push({ key: k, label: FIELD_LABELS[k] || k, before: a, after: b });
    }
    return out;
  }, [showDiff, lastSaved, draft]);

  async function onDelete() {
    if (!confirm(`Delete ${fund.name}? This cannot be undone (the row will be removed from Airtable).`)) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteFund(fund.id);
      onDeleted(fund.id);
      toast.success('Fund deleted', fund.name);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Delete failed';
      setError(msg);
      toast.error('Delete failed', msg);
      setDeleting(false);
    }
  }

  const c = logoColor(fund.name);

  return (
    <>
      <div className={'fixed inset-0 bg-navy/30 backdrop z-30 hidden md:block ' + (open ? 'open' : '')} onClick={close} />
      <aside className={'drawer fixed top-0 right-0 z-40 h-full w-full md:w-[640px] md:max-w-[92vw] bg-white shadow-drawer flex flex-col ' + (open ? 'open' : '')}>
        <header className="px-6 py-4 border-b border-line-soft flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-md grid place-items-center font-display text-[13px]" style={{ background: c.bg, color: c.fg }}>{initials(fund.name)}</div>
            <div className="min-w-0">
              <div className="h-display text-[16px] leading-tight">{fund.name}</div>
              <div className="text-[11.5px] text-ink-muted leading-tight font-mono">{fund.id} · last verified {fund.lastVerified ?? '-'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(fund.id)}
              title="Copy Airtable record id"
              className="h-8 px-3 text-[12.5px] rounded-md border border-line bg-white hover:bg-cream-tint inline-flex items-center font-mono text-ink-muted"
            >Copy ID</button>
            <button onClick={close} className="h-8 w-8 grid place-items-center rounded-md hover:bg-cream-tint" title="Close (Esc)">
              <IconClose className="w-4 h-4 text-ink-muted" />
            </button>
          </div>
        </header>

        <div className="px-6 border-b border-line-soft flex items-center gap-5 text-[13px]">
          <TabBtn active={tab === 'identity'} onClick={() => setTab('identity')}>Identity</TabBtn>
          <TabBtn active={tab === 'investment'} onClick={() => setTab('investment')}>Investment</TabBtn>
          <TabBtn active={tab === 'thesis'} onClick={() => setTab('thesis')}>Thesis</TabBtn>
          <TabBtn active={tab === 'meta'} onClick={() => setTab('meta')}>Verification</TabBtn>
        </div>

        <div className="flex-1 overflow-y-auto scroll-area px-6 py-5 space-y-5">
          {error && (
            <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-800 text-[12.5px]">{error}</div>
          )}

          {tab === 'identity' && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name"><TextInput value={draft.name} onChange={v => set('name', v)} /></Field>
              <Field label="Slug"><TextInput value={draft.slug ?? ''} onChange={v => set('slug', v)} mono /></Field>
              <Field label="City"><TextInput value={draft.city ?? ''} onChange={v => set('city', v)} /></Field>
              <Field label="Country"><TextInput value={draft.country ?? ''} onChange={v => set('country', v)} /></Field>
              <Field label="Type">
                <SelectInput value={draft.type ?? ''} onChange={v => set('type', v)} options={TYPE_OPTIONS} />
              </Field>
              <Field label="Category">
                <SelectInput value={draft.category ?? ''} onChange={v => set('category', v)} options={CATEGORY_OPTIONS} />
              </Field>
              <Field label="Founded"><TextInput value={draft.founded ?? ''} onChange={v => set('founded', v)} /></Field>
              <Field label="Vintage"><TextInput value={draft.vintage ?? ''} onChange={v => set('vintage', v)} /></Field>
              <div className="col-span-2"><Field label="Website"><TextInput value={draft.website ?? ''} onChange={v => set('website', v)} mono /></Field></div>
              <div className="col-span-2"><Field label="LinkedIn"><TextInput value={draft.linkedin ?? ''} onChange={v => set('linkedin', v)} mono /></Field></div>
            </div>
          )}

          {tab === 'investment' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Field label="Ticket min (USD)"><TextInput value={String(draft.ticketMin ?? '')} onChange={v => set('ticketMin', v ? Number(v) : undefined)} /></Field>
                <Field label="Ticket max (USD)"><TextInput value={String(draft.ticketMax ?? '')} onChange={v => set('ticketMax', v ? Number(v) : undefined)} /></Field>
                <Field label="Fund size (USD label)"><TextInput value={draft.fundSizeUsd ?? ''} onChange={v => set('fundSizeUsd', v)} /></Field>
              </div>
              <Field label="Stages">
                <Pills options={STAGE_OPTIONS} selected={draft.stages ?? []} onToggle={v => toggleArrItem('stages', v)} />
              </Field>
              <Field label="Sectors (comma-separated)">
                <TextInput
                  value={(draft.sectors ?? []).join(', ')}
                  onChange={v => set('sectors', v.split(',').map(s => s.trim()).filter(Boolean))}
                />
              </Field>
              <Field label="Quantum conviction">
                <SelectInput
                  value={draft.quantumConviction ?? ''}
                  onChange={v => set('quantumConviction', v)}
                  options={['Pure-play', 'Deep tech actif', 'Opportuniste', 'CVC stratégique', 'Dormant quantum']}
                />
              </Field>
              <Field label="Lead behaviour">
                <SelectInput
                  value={draft.lead ?? ''}
                  onChange={v => set('lead', v)}
                  options={['Lead', 'Co-lead', 'Follow', 'Mixed']}
                />
              </Field>
            </div>
          )}

          {tab === 'thesis' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="h-display text-[14px]">Thesis</h4>
                <div className="inline-flex border border-line rounded-md bg-white p-0.5 text-[12px]">
                  <button onClick={() => setThesisLang('en')} className={'px-2.5 py-1 rounded ' + (thesisLang === 'en' ? 'bg-cream-tint text-ink font-medium' : 'text-ink-muted')}>EN</button>
                  <button onClick={() => setThesisLang('fr')} className={'px-2.5 py-1 rounded ' + (thesisLang === 'fr' ? 'bg-cream-tint text-ink font-medium' : 'text-ink-muted')}>FR</button>
                </div>
              </div>
              <textarea
                value={(draft.thesis?.[thesisLang]) ?? ''}
                onChange={e => setThesis(thesisLang, e.target.value)}
                rows={14}
                placeholder={thesisLang === 'fr' ? 'Thèse en français…' : 'Thesis in English…'}
                className="w-full text-[13px] border border-line rounded-md p-2.5 ring-input resize-y"
              />
              <Field label="Short thesis (one-liner)">
                <TextInput value={draft.shortThesis ?? ''} onChange={v => set('shortThesis', v)} />
              </Field>
            </div>
          )}

          {tab === 'meta' && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Status">
                <SelectInput value={draft.status ?? ''} onChange={v => set('status', v)} options={['Active', 'Dormant', 'Closed']} />
              </Field>
              <Field label="Shortlist">
                <SelectInput value={draft.shortlist ?? ''} onChange={v => set('shortlist', v)} options={SHORTLIST_OPTIONS} />
              </Field>
              <Field label="Last verified"><TextInput value={draft.lastVerified ?? ''} onChange={v => set('lastVerified', v)} /></Field>
              <Field label="Verification source"><TextInput value={draft.verificationSource ?? ''} onChange={v => set('verificationSource', v)} mono /></Field>
              <div className="col-span-2"><Field label="Portfolio URL"><TextInput value={draft.portfolioUrl ?? ''} onChange={v => set('portfolioUrl', v)} mono /></Field></div>
            </div>
          )}
        </div>

        <footer className="px-6 py-3.5 border-t border-line-soft bg-white flex items-center justify-between">
          <div className="text-[12.5px] text-ink-muted flex items-center gap-2">
            <span className={'inline-block w-1.5 h-1.5 rounded-full ' + (saving ? 'bg-amber animate-pulse' : dirty ? 'bg-amber' : 'bg-funds')} />
            <span>
              {saving ? 'Saving…' : dirty ? 'Auto-save in 1.5s' : savedAt ? `Saved ${Math.max(1, Math.floor((Date.now() - savedAt) / 1000))}s ago` : 'Up to date'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onDelete} disabled={deleting} className="h-9 px-3 text-[13px] rounded-md text-red-700 hover:bg-red-50 disabled:opacity-60">
              {deleting ? 'Deleting…' : 'Delete fund'}
            </button>
            <button onClick={close} className="h-9 px-3 text-[13px] rounded-md border border-line bg-white hover:bg-cream-tint">Close</button>
            <button onClick={() => onSave()} disabled={!dirty || saving} className="h-9 px-3.5 text-[13px] rounded-md bg-navy text-white hover:bg-navy-soft font-medium flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <IconRefresh className="w-4 h-4 animate-spin" /> : <IconCheck className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save now'}
            </button>
          </div>
        </footer>
      </aside>

      <DiffModal
        open={showDiff}
        title={`Confirm changes to ${fund.name}`}
        diffs={diffEntries}
        rememberSkipKey={DIFF_SKIP_KEY}
        onCancel={() => setShowDiff(false)}
        onConfirm={async () => {
          setShowDiff(false);
          await commitSave();
        }}
      />
    </>
  );
}

function diffFund(a: Fund, b: Fund): Partial<Fund> {
  const out: Partial<Fund> = {};
  const keys = new Set<keyof Fund>([...(Object.keys(a) as (keyof Fund)[]), ...(Object.keys(b) as (keyof Fund)[])]);
  for (const k of keys) {
    if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) (out as any)[k] = b[k];
  }
  return out;
}

/* --- helpers --- */
function TabBtn({ active, children, onClick }: { active?: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick} className={'px-1 py-3 border-b-2 -mb-px ' + (active ? 'border-navy font-medium text-ink' : 'border-transparent text-ink-muted hover:text-ink')}>{children}</button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11.5px] uppercase tracking-wider text-ink-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, mono }: { value: string; onChange: (v: string) => void; mono?: boolean }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      className={'w-full h-9 px-2.5 text-[13.5px] rounded-md border border-line bg-white ring-input ' + (mono ? 'font-mono text-[12.5px]' : '')}
    />
  );
}

function SelectInput({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className="w-full h-9 px-2.5 text-[13.5px] rounded-md border border-line bg-white ring-input">
      <option value="">Select…</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Pills({ options, selected, onToggle }: { options: string[]; selected: string[]; onToggle: (v: string) => void }) {
  const set = new Set(selected);
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(o => {
        const on = set.has(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => onToggle(o)}
            className={'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[12px] border transition ' +
              (on ? 'bg-funds-soft text-funds-deep border-funds' : 'bg-cream-deep text-ink border-transparent hover:bg-cream')}
          >
            {o} {on && <IconCheck className="w-3 h-3" />}
          </button>
        );
      })}
    </div>
  );
}
